// The parts every issuing panel is built out of: the card and heading, the
// loading placeholder, the mixed-units caveat, and the delta line.
//
// Split out of `IssuingPanels.tsx` because "add a panel" already means editing
// that file — the layout map in `IssuingAnalyticsPage` makes a new `PanelId` a
// compile error until something draws it, and the thing that draws it goes
// there. Four panels and their shared furniture in one 500-line file made every
// panel change touch the same lines. The furniture is stable; the panels are
// not. They belong in different files.
//
// Nothing here knows which panel it is decorating, and nothing here reads a
// `PanelResult`. That is the line between this file and its neighbour.

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/synapse/Icon';
import type { IssuingAnalyticsResponse, MeasureFormat, MeasureId } from '@/lib/analytics';
import type { ChartDelta } from '@/lib/issuingCharts';
import { cn } from '@/lib/utils';
import { formatMeasure } from './panelData';

/**
 * What the four panels all need: the payload they share, the measure the user is
 * looking at, and how to print a number of it.
 */
export interface PanelProps {
  panels: IssuingAnalyticsResponse | undefined;
  measure: MeasureId;
  formatValue: (value: number) => string;
  /** Renders a share as text, in the viewer's locale. */
  formatPercent: (share: number) => string;
  isLoading: boolean;
}

export function PanelSection({
  title,
  action,
  className,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn('flex min-w-0 flex-col', className)}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

export function PanelSkeleton({ height }: { height: string }) {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className={cn('animate-pulse rounded-md bg-paper-2', height)}
    />
  );
}

/**
 * The caveat that every `units` figure spanning more than one medicine carries.
 *
 * `units` is counted in each item's own unit — a clinic issues Panadol in
 * tablets and a syrup in millilitres — so a sum across items adds up things that
 * are not the same thing, and the shares taken from that sum inherit it. That is
 * a known and accepted limit of the measure, not a bug to be fixed in the
 * arithmetic; what it cannot be is unstated, because a bare "250" under the word
 * "Units" reads as a count.
 *
 * `lines` carries no such note: an issue row means the same thing whatever was
 * on it, which is what makes it the measure to compare items by.
 *
 * Which figures get the note is read off each measure's own `denomination`, not
 * off a list of measure ids spelled out here — see `mixesItemUnits`.
 *
 * The short label is always legible and the sentence is one click away — a
 * qualification that only exists inside a tooltip is a qualification the reader
 * who needed it never saw, and the tiles have no room for the sentence itself.
 */
export function MixedUnitsNote({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const noteId = useId();

  return (
    <div className={cn('flex flex-col items-start gap-1', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={noteId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="inline-flex items-center gap-1 rounded-sm text-[11px] text-muted-2 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icon name="info" size={12} className="shrink-0" />
        {t('analytics.mixedUnits.label')}
      </button>
      {open && (
        <p id={noteId} className="max-w-[260px] text-[11px] leading-snug text-muted">
          {t('analytics.mixedUnits.note')}
        </p>
      )}
    </div>
  );
}

const DELTA_ICON = { up: 'arrow_up_right', down: 'arrow_down', flat: 'minus' } as const;

/**
 * The period-over-period change. Falls back to the absolute change whenever
 * `percent` is null — the baseline was too small for a percentage to mean
 * anything, and "+100%" off a baseline of one is a rounding error dressed as a
 * trend.
 */
export function DeltaLine({
  delta,
  days,
  format,
}: {
  delta: ChartDelta;
  days: number;
  format: MeasureFormat;
}) {
  const { t, i18n } = useTranslation();

  if (delta.direction === 'flat') {
    return (
      <div className="mt-1 text-[11.5px] text-muted-2">
        {t('analytics.deltaFlat')} · {t('analytics.deltaVs', { count: days })}
      </div>
    );
  }

  const sign = delta.absolute > 0 ? '+' : '−';
  const magnitude =
    delta.percent === null
      ? formatMeasure(Math.abs(delta.absolute), format, i18n.language)
      : `${Math.abs(delta.percent)}%`;

  return (
    <div className="mt-1 flex items-center gap-1 text-[11.5px] text-muted">
      <Icon name={DELTA_ICON[delta.direction]} size={12} className="shrink-0 text-muted-2" />
      <span className="font-mono tabular-nums text-ink-2">
        {sign}
        {magnitude}
      </span>
      <span className="truncate text-muted-2">{t('analytics.deltaVs', { count: days })}</span>
    </div>
  );
}
